import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { SubmissionRuleActions } from '@services/template-data.service';
import { NgxPopupService } from '../../../../shared/ngx-popups/ngx-popups/ngx-popups';
import { Statement } from '../../../review/human-validation/models/statement';
import { DYNAMIC_ADJUSTMENT_STATEMENT } from '../../../../../utils/constants';
import { SCALE_MULTIPLIERS, ScaleType } from '@services/rounding.service';

export interface ValidationRule {
  message: string;
  action: SubmissionRuleActions;
  rowLabel?: string;
  cellValue?: string;
  rowRef?: string;
}

export interface ValidationResult {
  statementId: number;
  statementType: string;
  statementDate: string;
  periodKeyUuid?: string;
  status: 'passing' | 'hard-stop' | 'soft-stop';
  rules: ValidationRule[];
}

export interface ValidationPeriod {
  statementDate: string;
  periodKeyUuid?: string;
  status: 'passing' | 'hard-stop' | 'soft-stop';
  rules: ValidationRule[];
}

export interface StatementGroup {
  statement: Statement;
  statementType: string;
  periods: ValidationPeriod[];
}

export interface NavigationRequest {
  rowRef: string;
  statementDate: string;
  periodKeyUuid: string;
  statementId?: number;
  navigateTo?: string
}

@Component({
    selector: 'app-submission-rules-validation-modal',
    templateUrl: './submission-rules-validation-modal.component.html',
    styleUrls: ['./submission-rules-validation-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SubmissionRulesValidationModalComponent {
  @Input() validationResults: ValidationResult[] = [];
  @Input() availableStatements: Statement[] = [];
  @Input() roundingFactor: ScaleType = 'ACTUALS';
  @Output() proceed = new EventEmitter<void>();
  @Output() navigateToValidationIssue = new EventEmitter<NavigationRequest>();

  private decimalPipe: DecimalPipe;

  constructor(private popupService: NgxPopupService) {
    this.decimalPipe = new DecimalPipe('en-US');
  }

  readonly SubmissionRuleActions = SubmissionRuleActions;

  onClose(): void {
    this.popupService.closeAll();
  }

  onProceed(): void {
    this.popupService.closeAll();
    this.proceed.emit();
  }

  onValidationRuleClick(statementId: number, statementDate: string, periodKeyUuid?: string, rowRef?: string): void {
    this.popupService.closeAll();
    this.navigateToValidationIssue.emit({
      statementId,
      statementDate,
      periodKeyUuid,
      rowRef
    });
  }

  getStatementDisplayName(statement: Statement): string {
    if (!statement) {
      return 'Unknown';
    }
    if (statement.statementType === DYNAMIC_ADJUSTMENT_STATEMENT && statement?.uniqueDasName) {
      return statement.uniqueDasName;
    } else {
      const statementType = statement.statementType;
      switch (statementType) {
        case 'INCOME_STATEMENT':
          return 'Income Statement';
        case 'BALANCE_SHEET':
          return 'Balance Sheet';
        case 'CASH_FLOW_STATEMENT':
          return 'Cash Flow Statement';
        default:
          return statementType || 'Unknown';
      }
    }
  }

  getStatusIcon(action: SubmissionRuleActions): string {
    switch (action) {
      case SubmissionRuleActions.HARD_STOP:
        return 'fas fa-circle-xmark';
      case SubmissionRuleActions.SOFT_STOP:
        return 'fas fa-exclamation-triangle';
      default:
        return 'fas fa-check';
    }
  }

  get groupedResults(): StatementGroup[] {
    const statementsToShow = this.availableStatements || [];
    const groupedResults: StatementGroup[] = [];
    
    // Create a map of validation results by statement ID for quick lookup
    const validationResultsMap = new Map<number, ValidationResult[]>();
    this.validationResults.forEach(result => {
      if (!validationResultsMap.has(result.statementId)) {
        validationResultsMap.set(result.statementId, []);
      }
      validationResultsMap.get(result.statementId)!.push(result);
    });
    
    // Process each statement in the order they appear in statementsToShow
    statementsToShow.forEach(statement => {
      const periods: ValidationPeriod[] = [];
      const statementValidationResults = validationResultsMap.get(statement.id) || [];
      
      // Check if all periods for this statement are passing
      const allPeriodsPassing = statementValidationResults.length > 0 && 
        statementValidationResults.every(result => result.status === 'passing');
      
      if (allPeriodsPassing) {
        // If all periods are passing, show just one "All Periods" block
        periods.push({
          statementDate: 'All Periods',
          status: 'passing',
          rules: []
        });
      } else if (statementValidationResults.length > 0) {
        // If there are mixed results (some passing, some failing), show individual period blocks
        statementValidationResults.forEach(result => {
          periods.push({
            statementDate: result.statementDate,
            periodKeyUuid: result.periodKeyUuid,
            status: result.status,
            rules: result.rules
          });
        });
      } else {
        // If no validation results at all for this statement, add a default "passing"
        periods.push({
          statementDate: 'All Periods',
          status: 'passing',
          rules: []
        });
      }
      
      groupedResults.push({
        statement: statement,
        statementType: statement.statementType,
        periods: periods
      });
    });
    
    return groupedResults;
  }

  get hasHardStops(): boolean {
    return this.validationResults.some(result => result.status === 'hard-stop');
  }

  get hasSoftStops(): boolean {
    return this.validationResults.some(result => result.status === 'soft-stop');
  }

  getRowDisplayText(rule: ValidationRule): string {
    if (!rule) {
      return '';
    }
    
    const cellValue = parseFloat(rule.cellValue || '0');
    const multiplier = SCALE_MULTIPLIERS[this.roundingFactor];
    const roundedValue = cellValue ? cellValue / multiplier : cellValue;
    const formattedValue = this.decimalPipe.transform(roundedValue, '1.0-20');
    
    return `${rule.rowLabel} = ${formattedValue}`;
  }
}
